import { useCallback, useEffect, useMemo } from 'react';

import useScopedLocalStorage from './localStorage/useScopedLocalStorage';

type UsePinnedItemsParams = {
  allKeys: readonly string[];
  storageKey: string;
  defaultOrder: readonly string[];
};

export type UsePinnedItemsReturn = {
  availableKeys: string[];
  isPinned: (key: string) => boolean;
  order: string[];
  pin: (key: string) => void;
  pinnedSet: Set<string>;
  reorder: (newOrder: string[]) => void;
  unpin: (key: string) => void;
};

type StoredPinState = {
  order: string[];
  seenKeys: string[];
};

// Older persisted values were a bare order array, with no record of which keys the
// user had already seen. Treat that shape as "nothing seen yet" so reconcileOrder
// folds every currently-unpinned key back in exactly once (see below) before
// settling into the new shape going forward.
const normalizeStored = (stored: StoredPinState | string[] | null): StoredPinState => {
  if (Array.isArray(stored)) return { order: stored, seenKeys: [] };

  return stored ?? { order: [], seenKeys: [] };
};

const sameStoredState = (a: StoredPinState, b: StoredPinState): boolean =>
  a.order.length === b.order.length &&
  a.order.every((key, index) => key === b.order[index]) &&
  a.seenKeys.length === b.seenKeys.length &&
  a.seenKeys.every((key) => b.seenKeys.includes(key));

const reconcileOrder = (
  stored: StoredPinState | string[] | null,
  allKeys: readonly string[],
  defaultOrder: readonly string[],
): StoredPinState => {
  const { order: storedOrder, seenKeys } = normalizeStored(stored);
  const allowed = new Set(allKeys);
  const alreadySeen = new Set(seenKeys);

  const reconciled = storedOrder.filter((key) => allowed.has(key));

  if (!reconciled.length) {
    return { order: defaultOrder.filter((key) => allowed.has(key)), seenKeys: [...allKeys] };
  }

  // Keys present in allKeys that this user has never seen before - whether newly
  // added to the config, or simply unknown because they came from legacy stored
  // data - are folded in so they show up without the user having to find and pin
  // them manually. Keys the user has already seen (and left unpinned on purpose)
  // are not resurrected.
  const newKeys = allKeys.filter((key) => !alreadySeen.has(key) && !reconciled.includes(key));

  return { order: [...reconciled, ...newKeys], seenKeys: [...allKeys] };
};

const usePinnedItems = ({
  allKeys,
  storageKey,
  defaultOrder,
}: UsePinnedItemsParams): UsePinnedItemsReturn => {
  const [stored, setStored] = useScopedLocalStorage<StoredPinState | string[]>(
    storageKey,
    { order: [...defaultOrder], seenKeys: [...allKeys] },
    { shareAcrossTeams: true },
  );

  const reconciledState = useMemo(
    () => reconcileOrder(stored, allKeys, defaultOrder),
    [allKeys, defaultOrder, stored],
  );

  // Upgrade legacy (bare-array) storage and catch seenKeys up to the current
  // config, so a later unpin() of a just-resurfaced key sticks instead of
  // reappearing on the next reconciliation. allKeys/defaultOrder come from
  // static per-section config and aren't expected to change mid-session.
  useEffect(() => {
    setStored((prev) => {
      const normalized = normalizeStored(prev);

      return sameStoredState(normalized, reconciledState) ? prev : reconciledState;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { order } = reconciledState;

  const pinnedSet = useMemo(() => new Set(order), [order]);

  const availableKeys = useMemo(
    () => allKeys.filter((key) => !pinnedSet.has(key)),
    [allKeys, pinnedSet],
  );

  const isPinned = useCallback((key: string) => pinnedSet.has(key), [pinnedSet]);

  const pin = useCallback(
    (key: string) => {
      if (pinnedSet.has(key)) return;

      setStored({ order: [...order, key], seenKeys: [...allKeys] });
    },
    [allKeys, order, pinnedSet, setStored],
  );

  const unpin = useCallback(
    (key: string) => {
      if (order.length <= 1) return;

      setStored({
        order: order.filter((existingKey) => existingKey !== key),
        seenKeys: [...allKeys],
      });
    },
    [allKeys, order, setStored],
  );

  const reorder = useCallback(
    (newOrder: string[]) => {
      setStored({ order: newOrder, seenKeys: [...allKeys] });
    },
    [allKeys, setStored],
  );

  return { availableKeys, isPinned, order, pin, pinnedSet, reorder, unpin };
};

export default usePinnedItems;

export const getNextActive = (
  order: string[],
  unpinnedKey: string,
  currentActive: string | null,
): string | null => {
  if (currentActive !== unpinnedKey) return currentActive;

  const remaining = order.filter((key) => key !== unpinnedKey);

  return remaining[0] ?? null;
};

export const sortByPinnedOrder = <T extends { hash: string }>(
  items: readonly T[],
  order: readonly string[],
): T[] => {
  const orderIndex = new Map(order.map((key, index) => [key, index]));

  return [...items].sort((a, b) => {
    const aIndex = orderIndex.get(a.hash);
    const bIndex = orderIndex.get(b.hash);

    if (aIndex === undefined && bIndex === undefined) return 0;
    if (aIndex === undefined) return 1;
    if (bIndex === undefined) return -1;

    return aIndex - bIndex;
  });
};
